import { h, Component, Fragment } from "preact";
import { observer } from "mobx-react";
import classnames from "classnames";

import { Autocomplete as LibraryAutocomplete } from "@searchspring/snap-preact-components";

import { Result } from "../Result/Result";

import { Tabs } from "../Tabs/Tabs";

import { IconCtaArrow } from "../Icons/IconCtaArrow";

import "./autocomplete.css";
import { Article } from "../Result/Article";
import { useEffect, useRef } from "preact/hooks";

@observer
export class Autocomplete extends Component {

  componentDidUpdate() {
    const ssAutocompleteWrapper = document.querySelector('.ss__autocomplete__content');
    if(ssAutocompleteWrapper && !ssAutocompleteWrapper.ariaLive) {
      ssAutocompleteWrapper.ariaLive = 'polite';
      ssAutocompleteWrapper.role = 'status';
    }
  }

  render() {
    const tabs = this.props.tabs;
    const controller = tabs.active.controller;
    const lastSearch = useRef();

    const autocompleteStrings =
      window.Resources?.searchspring?.autocomplete ?? {};

    const totalResults = controller.store.pagination.totalResults;
    const currentSearch = document.querySelector('.search__input').value;
    
    useEffect(() => { 
      if (totalResults === 0 && lastSearch.current === tabs.active.id) { 
        const nextTab = tabs.config.tabs.find(tab => tab.id !== tabs.active.id);
        //lastSearch.current = nextTab.id;
        tabs.switch(nextTab.id);
      }
    }, [controller.store.pagination]);

    useEffect(() => { 
      lastSearch.current = tabs.active.id;
    }, [currentSearch]);

    const resultsTitle = controller.store.state.input
      ? autocompleteStrings.recommendedProducts
      : autocompleteStrings.popularProducts;

    const breakpoints = {
      0: {
        columns: 1,
        rows: 4,
        hideFacets: true,
        vertical: true,
        hideHistory: false,
        hideTrending: false,
      },
      540: {
        columns: 1,
        rows: 4,
        vertical: true,
        hideHistory: false,
        hideTrending: false,
      },
      768: {
        columns: 2,
        rows: 3,
      },
    };

    return (
      <LibraryAutocomplete
        controller={controller}
        input={controller.config.selector}
        breakpoints={breakpoints}
        hideFacets
        hideBanners
        termsTitle={autocompleteStrings.suggestions}
        trendingTitle={autocompleteStrings.topSearches}
        contentTitle={resultsTitle}
        resultsSlot={<AutocompleteResults tabs={this.props.tabs} controller={controller}/>}
        noResultsSlot={<AutocompleteNoResults tabs={this.props.tabs} controller={controller}/>}
        termsSlot={<AutocompleteTerms />}
      />
    );
  }
}

// For reference, you can see where this slot component gets used by the library Autocomplete component here:
// https://github.com/searchspring/snap/blob/main/packages/snap-preact-components/src/components/Organisms/Autocomplete/Autocomplete.tsx#L503
const AutocompleteResults = ({ results, contentTitle, controller, tabs }) => {
  const numberOfResultsToShow = 4; // magic number - could later be shopify setting driven
  const resultsToShow = results.slice(0, numberOfResultsToShow);
  const { state } = controller.store;

  const autocompleteStrings =
    window.Resources?.searchspring?.autocomplete ?? {};
  return (
    <>
      <div><Tabs tabs={tabs} controller={controller} inAc={true}/></div>
      <div className="ss__autocomplete__results-wrap">
        {contentTitle && (
          <div
            className={
              "ss__autocomplete__title ss__autocomplete__title--content"
            }
          >
            <h5 className="ss__autocomplete__recommended--heading">
              {contentTitle}
            </h5>
          </div>
        )}

        <div className="ss__autocomplete__results">
          {resultsToShow.map((result) => (
            <div className="ss__autocomplete__result">
              {(controller.store.config?.id == 'ac-articles' || controller.store.config?.id == 'ac-articles-mobile') ? <Article result={result} hideContentType={false} hideContent={true}/> : <Result result={result} hideBadge hideSwatches hideWishlist hideLengths />}
            </div>
          ))}
        </div>
        {!!state.input && results.length > 0 && (
          <div className="ss__autocomplete__view-all">
            <a
              className="ss__autocomplete__view-all__link link link--text"
              href={state.url.href}
            >
              {autocompleteStrings.viewAllResults}
              <IconCtaArrow/>
            </a>
          </div>
        )}
      </div>
    </>
  );
};

const AutocompleteNoResults = ({ controller, tabs }) => {
  return ( 
    <>
      <div><Tabs tabs={tabs} controller={controller} inAc={true}/></div>
      <p aria-live="polite" role="status" className="ss__autocomplete__no-result">
        {window.Resources?.searchspring?.autocomplete?.noResultsAutocomplete}
      </p>
    </>
  );
};

const customEmIfy = (term, search) => {
  if (term && search) {
    const match = term.match(new RegExp(escapeRegExp(search), 'gi'));
    if (match) {
      const parts = term.split(new RegExp(`(${escapeRegExp(search)})`, 'gi'));
      return (
        <span>
          {parts.map((part, index) =>
            part.toLowerCase() === search.toLowerCase() ?
              part :
              <em key={index}>{part}</em>
          )}
        </span>
      );
    }
  }

  return (
    <em>{term}</em>
  );
};

const escapeRegExp = (str) => {
  return str?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// For reference, you can see where this slot component gets used by the library Autocomplete component here:
// https://github.com/searchspring/snap/blob/main/packages/snap-preact-components/src/components/Organisms/Autocomplete/Autocomplete.tsx#L402
const AutocompleteTerms = (props) => {
  const {
    terms,
    trending,
    termsTitle,
    trendingTitle,
    showTrending,
    valueProps,
    onTermClick,
    controller,
  } = props;
  const { state } = controller.store;
  return (
    <>
      {terms.length > 0 ? (
        <>
          <div className="ss__autocomplete__inner-wrapper">
            {termsTitle ? (
              <div className="ss__autocomplete__title ss__autocomplete__title--terms">
                <h5 className="ss__autocomplete__terms-heading">
                  {termsTitle}
                </h5>
              </div>
            ) : null}
            <div className="ss__autocomplete__terms__options">
              {terms.map((term) => (
                <div
                  role="status" aria-live="polite" className={classnames("ss__autocomplete__terms__option", "CQL-p3", {
                    "ss__autocomplete__terms__option--active": term.active,
                  })}
                >
                  <a
                    onClick={(e) => onTermClick && onTermClick(e)}
                    href={term.url.href}
                    {...valueProps}
                    onFocus={() => term.preview()}
                  >
                    {customEmIfy(term.value, state.input)}
                  </a>
                </div>
              ))}
            </div>
            <hr className="ss__autocomplete__inner-wrapper__divider medium-hide large-up-hide" />
          </div>
        </>
      ) : null}

      {showTrending ? (
        <div className="ss__autocomplete__inner-wrapper">
          {trendingTitle ? (
            <div className="ss__autocomplete__title ss__autocomplete__title--trending">
              <h5 className="ss__autocomplete__terms--heading" tabindex="0" role="status" aria-label={trendingTitle}>
                {trendingTitle}
              </h5>
            </div>
          ) : null}
          <div className="ss__autocomplete__terms__options">
            {trending.map((term) => (
              <div
                className={classnames("ss__autocomplete__terms__option", {
                  "ss__autocomplete__terms__option--active": term.active,
                })}
              >
                <a
                  onClick={(e) => onTermClick && onTermClick(e)}
                  href={term.url.href}
                  {...valueProps}
                  onFocus={() => term.preview()}
                >
                  {customEmIfy(term.value, state.input)}
                </a>
              </div>
            ))}
          </div>
          <hr className="ss__autocomplete__inner-wrapper__divider medium-hide large-up-hide" />
        </div>
      ) : null}
    </>
  );
};

